/**
 * PR-AdminUsers-6 — /admin/segments
 * Catálogo de segmentos con áreas expandibles y CRUD inline.
 */
import { useState } from "react";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  useAdminSegments,
  useCreateSegment,
  useUpdateSegment,
  useSegmentAreas,
} from "@/hooks/useAdminUsers";
import { cn } from "@/lib/utils";

export function AdminSegmentsPage() {
  const segments = useAdminSegments();
  const createSegment = useCreateSegment();
  const [showNew, setShowNew] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  const handleCreate = async () => {
    if (!newCode.trim() || !newName.trim()) return;
    setCreateError(null);
    try {
      await createSegment.mutateAsync({ code: newCode.trim(), name: newName.trim() });
      setNewCode("");
      setNewName("");
      setShowNew(false);
    } catch (e) {
      if (e instanceof ApiError) setCreateError(e.message);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Segmentos</h1>
          <p className="text-sm text-muted-foreground">
            Módulos funcionales del sidebar. Cada segmento puede tener áreas.
          </p>
        </div>
        <Button onClick={() => setShowNew(true)}>
          <Plus className="size-4" /> Nuevo segmento
        </Button>
      </div>

      {showNew && (
        <div className="rounded-lg border bg-card p-4 space-y-3">
          <p className="text-sm font-medium">Nuevo segmento</p>
          <div className="flex items-end gap-3">
            <div>
              <label className="text-xs text-muted-foreground mb-1 block">Código (único)</label>
              <Input
                placeholder="ej. analytics"
                value={newCode}
                onChange={e => setNewCode(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="flex-1">
              <label className="text-xs text-muted-foreground mb-1 block">Nombre</label>
              <Input
                placeholder="ej. Analytics"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === "Enter" && void handleCreate()}
              />
            </div>
            <Button onClick={() => void handleCreate()} disabled={createSegment.isPending}>
              Crear
            </Button>
            <Button variant="ghost" onClick={() => setShowNew(false)}>Cancelar</Button>
          </div>
          {createError && <p className="text-xs text-destructive">{createError}</p>}
        </div>
      )}

      {segments.isPending && <p className="text-sm text-muted-foreground">Cargando…</p>}
      {segments.isError && <p className="text-sm text-destructive">Error al cargar segmentos.</p>}

      <div className="space-y-2">
        {segments.data?.items.map(seg => (
          <SegmentRow key={seg.id} segment={seg} />
        ))}
      </div>
    </div>
  );
}

function SegmentRow({
  segment,
}: {
  segment: { id: string; code: string; name: string; description: string | null; is_active: boolean };
}) {
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState(segment.name);
  const [saveError, setSaveError] = useState<string | null>(null);

  const areas = useSegmentAreas(expanded ? segment.id : null);
  const updateSegment = useUpdateSegment(segment.id);

  const handleSave = async () => {
    setSaveError(null);
    try {
      await updateSegment.mutateAsync({ name: editName.trim() });
      setEditing(false);
    } catch (e) {
      if (e instanceof ApiError) setSaveError(e.message);
    }
  };

  const handleToggleActive = async () => {
    try {
      await updateSegment.mutateAsync({ is_active: !segment.is_active });
    } catch {
      // ignore
    }
  };

  return (
    <div className="rounded-lg border overflow-hidden">
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-accent/30"
        onClick={() => setExpanded(v => !v)}
      >
        {expanded ? (
          <ChevronDown className="size-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="size-4 text-muted-foreground" />
        )}
        <span className="font-mono text-xs text-muted-foreground w-32 shrink-0">{segment.code}</span>
        {editing ? (
          <Input
            value={editName}
            onChange={e => setEditName(e.target.value)}
            className="flex-1 h-7 text-sm"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => { if (e.key === "Enter") void handleSave(); if (e.key === "Escape") setEditing(false); }}
            autoFocus
          />
        ) : (
          <span className="flex-1 text-sm font-medium">{segment.name}</span>
        )}
        <span className={cn(
          "rounded-full px-2 py-0.5 text-[10px] font-semibold",
          segment.is_active
            ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
            : "bg-muted text-muted-foreground",
        )}>
          {segment.is_active ? "Activo" : "Inactivo"}
        </span>
        {/* Actions */}
        <div className="flex gap-1" onClick={e => e.stopPropagation()}>
          {editing ? (
            <>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => void handleSave()}>
                Guardar
              </Button>
              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditing(false)}>
                Cancelar
              </Button>
            </>
          ) : (
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setEditing(true)}>
              Editar
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs text-muted-foreground"
            onClick={() => void handleToggleActive()}
          >
            {segment.is_active ? "Desactivar" : "Activar"}
          </Button>
        </div>
      </div>
      {saveError && (
        <p className="text-xs text-destructive px-4 pb-2">{saveError}</p>
      )}
      {expanded && (
        <div className="border-t bg-muted/20 px-4 py-3">
          {areas.isPending && <p className="text-xs text-muted-foreground">Cargando áreas…</p>}
          {areas.data?.items.length === 0 && (
            <p className="text-xs text-muted-foreground">Sin áreas definidas.</p>
          )}
          {areas.data?.items.map(area => (
            <div key={area.id} className="flex items-center gap-3 py-1.5 text-sm">
              <span className="font-mono text-xs text-muted-foreground w-24">{area.code}</span>
              <span>{area.name}</span>
              <span className={cn(
                "ml-auto rounded-full px-2 py-0.5 text-[10px] font-semibold",
                area.is_active
                  ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400"
                  : "bg-muted text-muted-foreground",
              )}>
                {area.is_active ? "Activa" : "Inactiva"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
